import { z } from "zod";

import {
  MAX_CARTESIAN_CHART_CATEGORIES,
  MAX_CARTESIAN_CHART_CELLS,
  MAX_CARTESIAN_CHART_SERIES,
  MAX_CHART_COLORS,
  MAX_PIE_CHART_SLICES,
} from "../constants/chart";
import {
  EChartType,
  type ICartesianChartData,
  type TChartData,
  type TChartValueFormat,
  type TParseChartDataResult,
} from "../types/chart";

const VALUE_FORMAT_PATTERN = /^(?:number|integer|percent|currency:[A-Z]{3})$/;

const cartesianCategorySchema = z.string().trim().min(1);

const cartesianChartSeriesSchema = z
  .object({
    name: z.string().trim().min(1),
    data: z
      .array(z.number().finite())
      .min(1)
      .max(MAX_CARTESIAN_CHART_CATEGORIES),
  })
  .strict();

const pieChartSliceSchema = z
  .object({
    name: z.string().trim().min(1),
    value: z.number().finite(),
  })
  .strict();

const chartCaptionSchema = z.string().trim().min(1).optional();

// NOTE: Six-digit hex, with or without the hash — the only form every client
// and the server can turn into a colour without a stylesheet. A null entry
// keeps that series or slice in the palette colour it would have had, so a
// request to recolour only some slices leaves the others alone (NSAI-6779).
const chartColorsSchema = z
  .array(
    z
      .string()
      .trim()
      .regex(/^#?[0-9a-fA-F]{6}$/)
      .nullable(),
  )
  .min(1)
  .max(MAX_CHART_COLORS)
  .optional();

export const chartValueFormatSchema = z.custom<TChartValueFormat>(
  (value) => typeof value === "string" && VALUE_FORMAT_PATTERN.test(value),
  {
    message:
      'format is one of "number", "integer", "percent" or "currency:XXX" with an ISO currency code',
  },
);

const cartesianChartBaseSchema = z.object({
  title: chartCaptionSchema,
  subtitle: chartCaptionSchema,
  source: chartCaptionSchema,
  colors: chartColorsSchema,
  format: chartValueFormatSchema.optional(),
  categories: z
    .array(cartesianCategorySchema)
    .min(1)
    .max(MAX_CARTESIAN_CHART_CATEGORIES),
  series: z
    .array(cartesianChartSeriesSchema)
    .min(1)
    .max(MAX_CARTESIAN_CHART_SERIES),
});

interface IAddUniqueStringIssueProps {
  values: string[];
  path: string[];
  duplicateMessage: string;
  context: z.RefinementCtx;
}

const addUniqueStringIssue = (props: IAddUniqueStringIssueProps): void => {
  const { values, path, duplicateMessage, context } = props;
  const uniqueValues = new Set(values.map((value) => value.toLowerCase()));

  if (uniqueValues.size === values.length) {
    return;
  }

  context.addIssue({
    code: z.ZodIssueCode.custom,
    message: duplicateMessage,
    path,
  });
};

interface IAddCartesianChartIssuesProps {
  chartData: ICartesianChartData;
  context: z.RefinementCtx;
}

const addCartesianChartIssues = (
  props: IAddCartesianChartIssuesProps,
): void => {
  const { chartData, context } = props;
  const expectedPointCount = chartData.categories.length;
  const totalCellCount = expectedPointCount * chartData.series.length;

  addUniqueStringIssue({
    values: chartData.categories,
    path: ["categories"],
    duplicateMessage: "Chart categories must be unique",
    context,
  });

  addUniqueStringIssue({
    values: chartData.series.map((series) => series.name),
    path: ["series"],
    duplicateMessage: "Chart series names must be unique",
    context,
  });

  chartData.series.forEach((series, index) => {
    if (series.data.length === expectedPointCount) {
      return;
    }

    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Each series must include one value for every category",
      path: ["series", index, "data"],
    });
  });

  if (totalCellCount > MAX_CARTESIAN_CHART_CELLS) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Chart exceeds the maximum of ${MAX_CARTESIAN_CHART_CELLS} total values`,
      path: ["series"],
    });
  }
};

const pieChartSchema = z
  .object({
    type: z.literal(EChartType.PIE),
    title: chartCaptionSchema,
    subtitle: chartCaptionSchema,
    source: chartCaptionSchema,
    colors: chartColorsSchema,
    slices: z.array(pieChartSliceSchema).min(1).max(MAX_PIE_CHART_SLICES),
  })
  .strict()
  .superRefine((chartData, context) => {
    addUniqueStringIssue({
      values: chartData.slices.map((slice) => slice.name),
      path: ["slices"],
      duplicateMessage: "Pie chart slice names must be unique",
      context,
    });

    const hasNegativeValue = chartData.slices.some((slice) => slice.value < 0);
    const total = chartData.slices.reduce((sum, slice) => sum + slice.value, 0);

    if (hasNegativeValue) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Pie chart values must be non-negative",
        path: ["slices"],
      });
    }

    if (total <= 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Pie chart values must sum to more than zero",
        path: ["slices"],
      });
    }
  });

const barChartSchema = cartesianChartBaseSchema
  .extend({
    type: z.literal(EChartType.BAR),
  })
  .strict()
  .superRefine((chartData, context) =>
    addCartesianChartIssues({ chartData, context }),
  );

const lineChartSchema = cartesianChartBaseSchema
  .extend({
    type: z.literal(EChartType.LINE),
  })
  .strict()
  .superRefine((chartData, context) =>
    addCartesianChartIssues({ chartData, context }),
  );

const areaChartSchema = cartesianChartBaseSchema
  .extend({
    type: z.literal(EChartType.AREA),
  })
  .strict()
  .superRefine((chartData, context) =>
    addCartesianChartIssues({ chartData, context }),
  );

export const chartDataSchema: z.ZodType<TChartData> = z.union([
  barChartSchema,
  lineChartSchema,
  areaChartSchema,
  pieChartSchema,
]);

export const parseChartData = (input: unknown): TParseChartDataResult => {
  const result = chartDataSchema.safeParse(input);

  if (result.success) {
    return { success: true, data: result.data };
  }

  return { success: false, error: result.error.message };
};
